import React, { FC, useId } from 'react';
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';

import LocationInput from '@rover/components/src/formFields/LocationInput';
import { TemporaryAddressProvider } from '@rover/components/src/formFields/LocationInput/hooks/useTemporaryAddress';
import {
  actions as locationInputActions,
  effects as locationInputEffects,
  selectors as locationInputSelectors,
} from '@rover/components/src/formFields/LocationInput/LocationInput.duck';
import Connect from '@rover/react-lib/src/components/utils/Redux/Connect';
import type { ResolveApiErrorMessage } from '@rover/react-lib/src/hooks/useResolveApiErrorMessage';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import type { ContainerProps } from './container.constants';

const effects = {
  fetchGeocodedLatLng: locationInputEffects.fetchLocationDeviceOrGeocode,
  loadOptions: locationInputEffects.fetchLocationAutocomplete,
};

const actions = {
  dismissErrorMessage: locationInputActions.dismissErrorMessage,
  clearOptions: locationInputActions.clearLocationAutocompleteOptions,
};

const selector = (
  state,
  id,
  includeCurrentLocation,
  i18n: I18n,
  resolveApiError: ResolveApiErrorMessage
) => ({
  geocodedLatLng: locationInputSelectors.getLocationGeocodeResponse(state, id),
  geocodeResultsAreLoading: locationInputSelectors.getLocationGeocodeResultsAreLoading(state, id),
  loading: locationInputSelectors.getIsLoading(state, id),
  options: locationInputSelectors.getLocationAutocompleteOptions(
    state,
    id,
    includeCurrentLocation,
    i18n
  ),
  region: frontendConfigurationSelectors.getCcTld(state),
  selectedOption: locationInputSelectors.getSelectedOption(state, id),
  errorMessage: resolveApiError(locationInputSelectors.getErrorMessage(state, id)),
});

type Props = ContainerProps<
  typeof LocationInput,
  // Note: Specifying the Object type for onChange cascades into several complex typescript errors
  { onChange: (Object) => void; onClear?: () => void }
> & {
  inputId?: string;
  countryCode?: string | null | undefined;
  validationType: 'popover' | 'inline';
  validationError?: string | null | undefined;
  includeCurrentLocation?: boolean;
  forceUseHomeAddress?: boolean;
  initialTemporaryAddress?: Partial<UserInformationAddress> | null;
};

const LocationInputContainer: FC<Props> = (props) => {
  const autoGeneratedId = useId();
  const { i18n } = useLingui();
  const {
    inputId = autoGeneratedId,
    includeCurrentLocation = true,
    countryCode,
    initialTemporaryAddress,
  } = props;

  return (
    <Connect
      effects={effects}
      actions={actions}
      selector={(state, { resolveApiError }) =>
        selector(state, inputId, includeCurrentLocation, i18n, resolveApiError)
      }
    >
      {/* removing dispatch from connectedProps is a fine thing to do here */}
      {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
      {({ dispatch, ...connectedProps }) => (
        <TemporaryAddressProvider initialAddress={initialTemporaryAddress}>
          <LocationInput
            {...connectedProps}
            {...props}
            inputId={inputId}
            countryCode={countryCode}
          />
        </TemporaryAddressProvider>
      )}
    </Connect>
  );
};

export default LocationInputContainer;
